import { Modal, View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ThemePreference } from '@/lib/theme';
import { Colors, Shadows } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getHeadingFont, getBodyFont } from '@/constants/typography';

interface ThemePickerProps {
  visible: boolean;
  currentTheme: ThemePreference;
  onClose: () => void;
  onSelectTheme: (theme: ThemePreference) => void;
  colorScheme: 'light' | 'dark';
}

const THEME_OPTIONS: {
  value: ThemePreference;
  label: string;
  description: string;
  icon: keyof typeof Ionicons.glyphMap;
}[] = [
  {
    value: 'system',
    label: 'System',
    description: 'Follow device settings',
    icon: 'phone-portrait-outline',
  },
  {
    value: 'light',
    label: 'Light',
    description: 'Always use light theme',
    icon: 'sunny-outline',
  },
  {
    value: 'dark',
    label: 'Dark',
    description: 'Always use dark theme',
    icon: 'moon-outline',
  },
];

/**
 * ThemePicker Modal
 * Allows user to select theme preference (system/light/dark).
 * Displays current selection with radio button indicators.
 */
export function ThemePicker({
  visible,
  currentTheme,
  onClose,
  onSelectTheme,
  colorScheme,
}: ThemePickerProps) {
  const backgroundColor = Colors[colorScheme].background;
  const textColor = Colors[colorScheme].text;
  const accentColor = Colors[colorScheme].primary;
  const borderColor = Colors[colorScheme].border;

  const handleSelect = (theme: ThemePreference) => {
    onSelectTheme(theme);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}>
      <View style={styles.overlay}>
        <TouchableOpacity
          style={styles.backdrop}
          activeOpacity={1}
          onPress={onClose}
        />
        <View style={[styles.modalContainer, { backgroundColor }]}>
          <View style={[styles.header, { borderBottomColor: borderColor }]}>
            <Text style={[styles.title, { color: textColor, fontFamily: getHeadingFont() }]}>Theme</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={textColor} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.content}>
            {THEME_OPTIONS.map((option, index) => {
              const isSelected = currentTheme === option.value;
              const isFirst = index === 0;
              const isLast = index === THEME_OPTIONS.length - 1;

              return (
                <TouchableOpacity
                  key={option.value}
                  style={[
                    styles.optionItem,
                    { borderColor },
                    isFirst && styles.firstOption,
                    isLast && styles.lastOption,
                  ]}
                  onPress={() => handleSelect(option.value)}
                  activeOpacity={0.6}>
                  <View style={styles.iconContainer}>
                    <Ionicons
                      name={option.icon}
                      size={24}
                      color={isSelected ? accentColor : textColor}
                    />
                  </View>
                  <View style={styles.optionText}>
                    <Text style={[styles.optionLabel, { color: textColor, fontFamily: getBodyFont() }]}>
                      {option.label}
                    </Text>
                    <Text style={[styles.optionDescription, { color: textColor, fontFamily: getBodyFont() }]}>
                      {option.description}
                    </Text>
                  </View>
                  <View style={[styles.radioOuter, { borderColor: accentColor }]}>
                    {isSelected && (
                      <View style={[styles.radioInner, { backgroundColor: accentColor }]} />
                    )}
                  </View>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  modalContainer: {
    width: '85%',
    maxWidth: 400,
    borderRadius: BorderRadius.lg,
    ...Shadows.lg,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: Spacing.xl,
    paddingVertical: Spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
  },
  closeButton: {
    padding: Spacing.xs,
  },
  content: {
    maxHeight: 400,
  },
  optionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: Spacing.lg,
    paddingHorizontal: Spacing.xl,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  firstOption: {
    paddingTop: Spacing.xl,
  },
  lastOption: {
    borderBottomWidth: 0,
    paddingBottom: Spacing.xl,
  },
  iconContainer: {
    width: 40,
    alignItems: 'center',
    marginRight: Spacing.lg,
  },
  optionText: {
    flex: 1,
  },
  optionLabel: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: Spacing.xxs,
  },
  optionDescription: {
    fontSize: 12,
    opacity: 0.6,
  },
  radioOuter: {
    width: 22,
    height: 22,
    borderRadius: 11, // circular
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6, // circular
  },
});
